import { Component, OnInit, ViewEncapsulation, inject, ChangeDetectorRef, Input, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDialog } from "@angular/material/dialog";
import { TableComponent } from "@components/layouts/table/table.component";
import {
  EnumRequestStatus,
  ITableActionRow,
  TSignRequestTableRow,
} from "@modals/app.modal";
import { HttpService } from "@services/http.service";
import { Constants } from "@config/constants";
import { ISignRequest, ISigner } from "@modals/api.modal";
import { LoaderComponent } from "@components/layouts/loader/loader.component";
import { Messages } from "@config/messages";
import { StatusMessageComponent } from "@components/layouts/status-message/status-message.component";
import FileSaver from "file-saver";
import { AppService } from "@services/app.service";
import { ToastService } from "@services/toast.service";
import { TranslateModule } from "@ngx-translate/core";
import { AuthService } from "@services/auth.service";
import {
  RecallSignRequestDialogComponent,
  IRecallSignRequestDialogResult,
} from "@components/recall-sign-request-dialog/recall-sign-request-dialog.component";
import { RequestDetailsDialogComponent } from "@components/request-details-dialog/request-details-dialog.component";
import { RequestReportExportService } from "@services/request-report-export.service";
import {
  SignDownloadService,
} from "@services/sign-download.service";

const REQUEST_OLDER_THAN_DAYS = 30;

const REQUEST_STATUS_LABEL_KEYS: Record<string, string> = {
  All: "statusAll",
  Open: "statusOpen",
  Signed: "statusSigned",
  PartialSigned: "statusPartialSigned",
  Declined: "statusDeclined",
  Cancelled: "statusCancelled",
};

/** Status values available in the filter dropdown (multi-select → API) */
const REQUEST_STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: string; labelKey: string }> = [
  { value: EnumRequestStatus.OPEN, labelKey: "statusOpen" },
  { value: EnumRequestStatus.SIGNED, labelKey: "statusSigned" },
  { value: EnumRequestStatus.DECLINED, labelKey: "statusDeclined" },
  { value: EnumRequestStatus.CANCELLED, labelKey: "statusCancelled" },
];

const REQUESTS_FILTER_STORAGE_KEY = "kzn-requests-page-filters";
const REQUESTS_SORT_STORAGE_KEY = "kzn-requests-table-sort";

interface IStoredRequestsFilters {
  reqDateFrom?: string;
  reqDateTo?: string;
  completeDateFrom?: string;
  completeDateTo?: string;
  fromDate?: string;
  toDate?: string;
  activeChipStatus?: string;
  statuses?: string[];
  status?: string;
}

@Component({
  selector: "kzn-requests",
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatTooltipModule,
    TableComponent,
    LoaderComponent,
    StatusMessageComponent,
    TranslateModule,
  ],
  templateUrl: "./requests.component.html",
  styleUrl: "./requests.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class RequestsComponent implements OnInit {
  private _httpService = inject(HttpService);
  private _appService = inject(AppService);
  private _toastr = inject(ToastService);
  private _cdr = inject(ChangeDetectorRef);
  private _dialog = inject(MatDialog);
  private _authService = inject(AuthService);
  private _reportExport = inject(RequestReportExportService);
  private _signDownloadService = inject(SignDownloadService);

  public isLoading: boolean = true;
  public allRequests: Array<ISignRequest> = [];
  public tableRows: Array<TSignRequestTableRow> = [];
  public isError: boolean = false;
  public errorMessage: string = "";
  /** In-memory table filter — stat card selection; does not trigger API */
  public activeChipStatus: string = EnumRequestStatus.ALL;

  public reqDateFrom: Date;
  public reqDateTo: Date;
  public completeDateFrom: Date | null = null;
  public completeDateTo: Date | null = null;
  public appliedReqDateFrom!: Date;
  public appliedReqDateTo!: Date;
  public appliedCompleteDateFrom: Date | null = null;
  public appliedCompleteDateTo: Date | null = null;
  /** Empty = all statuses sent to API as blank SignRequestStatus */
  public selectedStatuses: string[] = [];
  public tableSearchQuery = "";
  public readonly statusFilterOptions = REQUEST_STATUS_FILTER_OPTIONS;
  @Input() showSearch: boolean = true;
  public exportInProgress = false;

  @ViewChild("requestsTable") private requestsTable?: TableComponent;

  getStatusLabelKey(status: string): string {
    return REQUEST_STATUS_LABEL_KEYS[status] ?? status;
  }

  get filterPeriodLabel(): string {
    if (!this.appliedReqDateFrom || !this.appliedReqDateTo) return "";
    const fmt = (d: Date) =>
      d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
    return `${fmt(this.appliedReqDateFrom)} – ${fmt(this.appliedReqDateTo)}`;
  }

  get showChipFilterEmpty(): boolean {
    return (
      this.activeChipStatus !== EnumRequestStatus.ALL &&
      this.getRequestsForActiveChip().length === 0
    );
  }

  get chipEmptyStatusLabelKey(): string {
    return REQUEST_STATUS_LABEL_KEYS[this.activeChipStatus] ?? this.activeChipStatus;
  }

  public statsCards: Array<{
    key: string;
    value: string;
    labelKey: string;
    color: string;
    bgColor: string;
    status: string;
  }> = [];
  readonly requestsSortStorageKey = REQUESTS_SORT_STORAGE_KEY;
  private readonly _baseTableColumns = [
    { columnDef: "RequestNo", header: "requestNo" },
    { columnDef: "CustomerName", header: "customerName" },
    {
      columnDef: "RequesterInfo",
      header: "requester",
      stackedCell: (row: TSignRequestTableRow) => ({
        primary: row.RequesterName?.trim() || "—",
        secondary: row.RequesterEmail?.trim() || "",
      }),
    },
    {
      columnDef: "RequestDate",
      header: "requestDate",
      isDate: true,
      cell: (row: TSignRequestTableRow) =>
        this._appService.getFormattedDate(row.RequestDate),
    },
    { columnDef: "RequestStatus", header: "requestStatus" },
  ];
  public tableColumns = [...this._baseTableColumns];
  public tableRowActions: Array<ITableActionRow> = [
    {
      icon: "visibility",
      color: "primary",
      title: "viewRequestDetails",
      actionFn: (row: TSignRequestTableRow) => this.openRequestDetailsDialog(row),
    },
    {
      icon: "download",
      color: "primary",
      title: "downloadSignedDoc",
      getTooltip: (row: TSignRequestTableRow): string =>
        this.isRequestOlderThanRetention(row)
          ? "requestDocumentRetentionTooltip"
          : "downloadSignedDoc",
      isLoading: (row: TSignRequestTableRow): boolean => !!row.isDownloading,
      isDisabled: (row: TSignRequestTableRow): boolean =>
        row.RequestStatus !== EnumRequestStatus.SIGNED ||
        !!row.isDownloading ||
        this.isRequestOlderThanRetention(row),
      actionFn: (row: TSignRequestTableRow) => this.downloadSignedFile(row),
    },
    {
      icon: "fact_check",
      imgIcon: "assets/audit-icon.png",
      color: "primary",
      title: "downloadAuditReport",
      getTooltip: (row: TSignRequestTableRow): string =>
        this.isRequestOlderThanRetention(row)
          ? "requestDocumentRetentionTooltip"
          : "downloadAuditReport",
      isLoading: (row: TSignRequestTableRow): boolean => !!row.isDownloadingAudit,
      isDisabled: (row: TSignRequestTableRow): boolean =>
        !!row.isDownloadingAudit ||
        !!row.isDownloading ||
        row.RequestStatus !== EnumRequestStatus.SIGNED ||
        this.isRequestOlderThanRetention(row),
      actionFn: (row: TSignRequestTableRow) => this.downloadAuditReport(row),
    },
    {
      icon: "undo",
      imgIcon: "assets/recal.svg",
      color: "primary",
      title: "recallRequest",
      isLoading: (row: TSignRequestTableRow): boolean => !!row.isRecalling,
      isDisabled: (row: TSignRequestTableRow): boolean =>
        !!row.isRecalling ||
        !!row.isDownloading ||
        !!row.isDownloadingAudit ||
        this.isRequestOlderThanRetention(row) ||
        (row.RequestStatus !== EnumRequestStatus.OPEN &&
          row.RequestStatus !== EnumRequestStatus.PARTIAL_SIGNED),
      actionFn: (row: TSignRequestTableRow) => this.openRecallSignRequestDialog(row),
    },
  ];

  constructor() {
    const defaults = this.getDefaultDateRange();
    this.reqDateFrom = defaults.fromDate;
    this.reqDateTo = defaults.toDate;
    this.syncAppliedDatesFromDraft();
  }

  ngOnInit(): void {
    this.restoreFiltersFromStorage();
    this.syncAppliedDatesFromDraft();
    this.getAllRequests();
  }

  private syncAppliedDatesFromDraft(): void {
    this.appliedReqDateFrom = new Date(this.reqDateFrom);
    this.appliedReqDateTo = new Date(this.reqDateTo);
    this.appliedCompleteDateFrom = this.completeDateFrom
      ? new Date(this.completeDateFrom)
      : null;
    this.appliedCompleteDateTo = this.completeDateTo
      ? new Date(this.completeDateTo)
      : null;
  }

  private getDefaultDateRange(): { fromDate: Date; toDate: Date } {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
    fromDate.setHours(0, 0, 0, 0);
    return { fromDate, toDate: new Date(today) };
  }

  private formatDateForApi(date: Date | null | undefined): string {
    if (!date) return "";
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  private parseStoredDate(value: string | undefined): Date | null {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return null;
    const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    parsed.setHours(0, 0, 0, 0);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private persistFiltersToStorage(): void {
    const payload: IStoredRequestsFilters = {
      reqDateFrom: this.formatDateForApi(this.reqDateFrom),
      reqDateTo: this.formatDateForApi(this.reqDateTo),
      completeDateFrom: this.formatDateForApi(this.completeDateFrom),
      completeDateTo: this.formatDateForApi(this.completeDateTo),
      activeChipStatus: this.activeChipStatus,
    };
    try {
      localStorage.setItem(REQUESTS_FILTER_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* ignore quota / private mode */
    }
  }

  private restoreFiltersFromStorage(): void {
    try {
      const raw = localStorage.getItem(REQUESTS_FILTER_STORAGE_KEY);
      if (!raw) return;
      const stored = JSON.parse(raw) as IStoredRequestsFilters;
      const from = this.parseStoredDate(stored.reqDateFrom ?? stored.fromDate);
      const to = this.parseStoredDate(stored.reqDateTo ?? stored.toDate);
      if (from) this.reqDateFrom = from;
      if (to) this.reqDateTo = to;
      this.completeDateFrom = this.parseStoredDate(stored.completeDateFrom);
      this.completeDateTo = this.parseStoredDate(stored.completeDateTo);
      this.selectedStatuses = [];
      const chipStatus = stored.activeChipStatus ?? stored.status;
      if (
        chipStatus &&
        chipStatus !== EnumRequestStatus.ALL &&
        Object.values(EnumRequestStatus).includes(chipStatus as EnumRequestStatus)
      ) {
        this.activeChipStatus = chipStatus;
      } else {
        this.activeChipStatus = EnumRequestStatus.ALL;
      }
      this.normalizeDateRange(false);
    } catch {
      /* ignore corrupt storage */
    }
  }

  private normalizeDateRange(persist: boolean): void {
    if (
      this.reqDateFrom &&
      this.reqDateTo &&
      this.reqDateFrom.getTime() > this.reqDateTo.getTime()
    ) {
      this.reqDateTo = new Date(this.reqDateFrom);
    }
    if (
      this.completeDateFrom &&
      this.completeDateTo &&
      this.completeDateFrom.getTime() > this.completeDateTo.getTime()
    ) {
      this.completeDateTo = new Date(this.completeDateFrom);
    }
    if (persist) {
      this.persistFiltersToStorage();
    }
  }

  private buildApiParams(): Record<string, string> {
    const params: Record<string, string> = {
      ReqDateFrom: this.formatDateForApi(this.appliedReqDateFrom),
      ReqDateTo: this.formatDateForApi(this.appliedReqDateTo),
    };
    const statuses = this.selectedStatuses.filter(Boolean);
    if (statuses.length) {
      params["SignRequestStatus"] = statuses.join(",");
    }
    const cDateFrom = this.formatDateForApi(this.appliedCompleteDateFrom);
    const cDateTo = this.formatDateForApi(this.appliedCompleteDateTo);
    if (cDateFrom) params["CDateFrom"] = cDateFrom;
    if (cDateTo) params["CDateTo"] = cDateTo;
    return params;
  }

  onFilterFieldChange(): void {
    this.normalizeDateRange(false);
    this.persistFiltersToStorage();
  }

  clearCompleteDateRange(): void {
    this.completeDateFrom = null;
    this.completeDateTo = null;
    this.appliedCompleteDateFrom = null;
    this.appliedCompleteDateTo = null;
    this.persistFiltersToStorage();
    this.getAllRequests(true);
  }

  onTableSearch(): void {
    this.requestsTable?.applyTextFilter(this.tableSearchQuery);
  }

 
  applyFilter(syncDates = true): void {
    this.normalizeDateRange(true);
    if (syncDates) {
      this.syncAppliedDatesFromDraft();
    }
    this.persistFiltersToStorage();
    this.getAllRequests(true);
  }

  clearFilter(): void {
    const defaults = this.getDefaultDateRange();
    this.reqDateFrom = defaults.fromDate;
    this.reqDateTo = defaults.toDate;
    this.completeDateFrom = null;
    this.completeDateTo = null;
    this.selectedStatuses = [];
    this.tableSearchQuery = "";
    this.activeChipStatus = EnumRequestStatus.ALL;
    this._cdr.detectChanges();
    this.applyFilter();
  }

  private generateAllStatuses(): void {
    this.updateStatsAndFilterOptions();
  }

  private getRequestsForActiveChip(): ISignRequest[] {
    if (this.activeChipStatus === EnumRequestStatus.ALL) {
      return this.allRequests;
    }
    const status = this.activeChipStatus.toLowerCase();
    return this.allRequests.filter(
      (r) => r.RequestStatus?.toLowerCase() === status
    );
  }

  private updateStatsAndFilterOptions(): void {
    const openCount = this.getCountByStatus(EnumRequestStatus.OPEN);
    const signedCount = this.getCountByStatus(EnumRequestStatus.SIGNED);
    const cancelledCount = this.getCountByStatus(EnumRequestStatus.CANCELLED);
    const declinedCount = this.getCountByStatus(EnumRequestStatus.DECLINED);
    const allCount = this.getCountByStatus(EnumRequestStatus.ALL);

    this.statsCards = [
      {
        key: "open",
        value: String(openCount),
        labelKey: "openRequestStat",
        color: "#e6a200",
        bgColor: "#fff8e1",
        status: EnumRequestStatus.OPEN,
      },
      {
        key: "signed",
        value: String(signedCount),
        labelKey: "signedRequestStat",
        color: "#7b1fa2",
        bgColor: "#f3e5f5",
        status: EnumRequestStatus.SIGNED,
      },
      {
        key: "cancelled",
        value: String(cancelledCount),
        labelKey: "cancelledStat",
        color: "#e53935",
        bgColor: "#fce4ec",
        status: EnumRequestStatus.CANCELLED,
      },
      {
        key: "declined",
        value: String(declinedCount),
        labelKey: "declinedRequestStat",
        color: "#6b7280",
        bgColor: "#f3f4f6",
        status: EnumRequestStatus.DECLINED,
      },
      {
        key: "all",
        value: String(allCount),
        labelKey: "allRequestStat",
        color: "#2e7d32",
        bgColor: "#e8f5e9",
        status: EnumRequestStatus.ALL,
      },
    ];
  }

  private getCountByStatus(status: string): number {
    if (status === EnumRequestStatus.ALL) return this.allRequests.length;
    return this.allRequests.filter((r) => r.RequestStatus?.toLowerCase() === status?.toLowerCase()).length;
  }

  getAllRequests(showLoader?: boolean): void {
    if (showLoader) this.isLoading = true;
    this.persistFiltersToStorage();

    this._httpService
      .get(
        this._httpService.createEndpoint(
          Constants.API_URI_SIGN_REQUEST_ALL,
          undefined,
          this.buildApiParams()
        )
      )
      .subscribe({
        next: (requestsRes: unknown) => {
          const requests = requestsRes as { Data?: ISignRequest[] };
          this.isLoading = false;
          this.isError = false;
          this.allRequests =
            requests.Data?.map((request: ISignRequest) => ({
              ...request,
              Signers: request.Signers?.map((signer: ISigner) => ({
                ...signer,
                SignRequestId: request.SignRequestId,
              })),
            })) ?? [];
          this.activeChipStatus = EnumRequestStatus.ALL;
          this.generateAllStatuses();
          this.applyTableRows();
        },
        error: (errorResponse: { error?: { FailRules?: { Msg?: string }[] } }) => {
          this.isLoading = false;
          this.isError = true;
          this.errorMessage =
            errorResponse.error?.FailRules?.[0]?.Msg ||
            Messages.MSG_ERROR_UNEXPECTED;
        },
      });
  }

  onStatCardClick(status: string): void {
    this.activeChipStatus = status;
    this.persistFiltersToStorage();
    this.applyTableRows();
  }

  async exportRequestsReport(): Promise<void> {
    this.exportInProgress = true;
    this.normalizeDateRange(true);
    this.syncAppliedDatesFromDraft();
    this._cdr.detectChanges();

    this._httpService
      .get(
        this._httpService.createEndpoint(
          Constants.API_URI_SIGN_REQUEST_EXPORT,
          undefined,
          this.buildApiParams()
        )
      )
      .subscribe({
        next: (response: unknown) => {
          void this.handleExportResponse(response);
        },
        error: (error) => {
          this._httpService.showHttpError(error);
          this.exportInProgress = false;
          this._cdr.detectChanges();
        },
      });
  }

  private async handleExportResponse(response: unknown): Promise<void> {
    try {
      const saved = await this.saveExportResponseAsXlsx(response);
      if (saved) {
        this._toastr.showSuccess(Messages.MSG_SUCCESS_EXPORT_REQUESTS_EXCEL);
      } else {
        this._toastr.showWarning(Messages.MSG_NO_RECORDS);
      }
    } catch {
      this._toastr.showError(Messages.MSG_ERROR_UNEXPECTED);
    } finally {
      this.exportInProgress = false;
      this._cdr.detectChanges();
    }
  }

  private async saveExportResponseAsXlsx(response: unknown): Promise<boolean> {
    const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
    const fallbackFileName = `requests-export-${stamp}.xlsx`;
    const xlsxMime =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (response instanceof Blob) {
      FileSaver.saveAs(response, fallbackFileName);
      return true;
    }

    if (Array.isArray(response)) {
      if (!response.length) return false;
      return this._reportExport.exportRawRowsToExcel(
        response as Record<string, unknown>[]
      );
    }

    const payload = response as {
      IsSuccess?: boolean;
      FailRules?: { Msg?: string }[];
      Data?: unknown;
    };

    if (payload?.IsSuccess === false) {
      const msg = payload.FailRules?.[0]?.Msg || Messages.MSG_ERROR_UNEXPECTED;
      this._toastr.showError(msg);
      return false;
    }

    const data = payload?.Data;
    if (data == null) return false;

    if (Array.isArray(data)) {
      if (!data.length) return false;
      return this._reportExport.exportRawRowsToExcel(
        data as Record<string, unknown>[]
      );
    }

    if (typeof data === "string" && data.trim()) {
      this.saveBase64AsXlsx(data, fallbackFileName, xlsxMime);
      return true;
    }

    if (typeof data === "object") {
      const fileData = data as Record<string, unknown>;
      const base64 =
        fileData["DocContent"] ??
        fileData["DocContentStr"] ??
        fileData["FileContent"] ??
        fileData["Content"] ??
        fileData["ExportContent"];
      const fileName =
        (typeof fileData["DocName"] === "string" && fileData["DocName"]) ||
        (typeof fileData["FileName"] === "string" && fileData["FileName"]) ||
        fallbackFileName;

      if (typeof base64 === "string" && base64.trim()) {
        this.saveBase64AsXlsx(base64, fileName, xlsxMime);
        return true;
      }
    }

    return false;
  }

  private saveBase64AsXlsx(
    base64: string,
    fileName: string,
    mimeType: string
  ): void {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    FileSaver.saveAs(
      blob,
      fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`
    );
  }

  private applyTableRows(): void {
    const filtered = this.getRequestsForActiveChip();
    this.updateCustomerColumnVisibility(filtered);
    this.tableRows = filtered.map((item: ISignRequest) => ({
      ...item,
      RequesterInfo: [item.RequesterName, item.RequesterEmail]
        .map((value) => value?.trim())
        .filter(Boolean)
        .join(" "),
    }));
    this._cdr.detectChanges();
    queueMicrotask(() => this.onTableSearch());
  }

  private updateCustomerColumnVisibility(requests: ISignRequest[]): void {
    const showCustomerColumn = requests.some(
      (request) => !!request.CustomerName?.trim()
    );
    this.tableColumns = showCustomerColumn
      ? [...this._baseTableColumns]
      : this._baseTableColumns.filter((column) => column.columnDef !== "CustomerName");
  }

  /** Local calendar date for RequestDate (matches getFormattedDate UTC semantics). */
  private parseRequestDateToLocalDay(value: string | undefined | null): Date | null {
    if (!value?.trim()) return null;
    const raw = value.trim();

    const ymdMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (ymdMatch) {
      const parsed = new Date(
        Number(ymdMatch[1]),
        Number(ymdMatch[2]) - 1,
        Number(ymdMatch[3])
      );
      parsed.setHours(0, 0, 0, 0);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const utcParsed = new Date(raw.endsWith("Z") ? raw : `${raw}Z`);
    if (Number.isNaN(utcParsed.getTime())) return null;
    const local = new Date(
      utcParsed.getFullYear(),
      utcParsed.getMonth(),
      utcParsed.getDate()
    );
    local.setHours(0, 0, 0, 0);
    return local;
  }

  private getRequestAgeInDays(row: TSignRequestTableRow): number | null {
    const requestDate = this.parseRequestDateToLocalDay(row.RequestDate);
    if (!requestDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.floor((today.getTime() - requestDate.getTime()) / (24 * 60 * 60 * 1000));
  }

  private isRequestOlderThanRetention(row: TSignRequestTableRow): boolean {
    const ageDays = this.getRequestAgeInDays(row);
    return ageDays !== null && ageDays > REQUEST_OLDER_THAN_DAYS;
  }

  private blockIfRequestOlderThanRetention(row: TSignRequestTableRow): boolean {
    if (!this.isRequestOlderThanRetention(row)) return false;
    this._toastr.showWarning(Messages.MSG_REQUEST_ACTION_RETENTION_EXPIRED);
    return true;
  }

  private openRequestDetailsDialog(row: TSignRequestTableRow): void {
    if (!row.SignRequestId?.trim()) return;
    this._dialog.open(RequestDetailsDialogComponent, {
      panelClass: ["rounded-dialog", "request-details-dialog-panel"],
      backdropClass: "blur-backdrop",
      width: "min(1080px, 96vw)",
      autoFocus: "first-tabbable",
      data: {
        signRequestId: row.SignRequestId,
        appToken: row.AppToken?.trim() ?? "",
        appName: row.AppName?.trim() ?? "",
        requestStatus: row.RequestStatus?.trim() ?? "",
        fallbackSigners: row.Signers ?? [],
      },
    });
  }

  private downloadSignedFile(row: TSignRequestTableRow): void {
    if (this.blockIfRequestOlderThanRetention(row)) return;
    const signRequestId = row.SignRequestId?.trim();
    if (!signRequestId) return;

    row.isDownloading = true;
    this._cdr.detectChanges();

    this._signDownloadService
      .downloadSignedFile$(signRequestId)
      .subscribe({
        next: (response: unknown) => {
          if (
            !this.saveDownloadResponseAsFile(
              response,
              `signed-${signRequestId}.pdf`
            )
          ) {
            if (!this.showResponseFailRules(response)) {
              this._toastr.showError(Messages.MSG_ERROR_UNEXPECTED);
            }
          }
          row.isDownloading = false;
          this._cdr.detectChanges();
        },
        error: (error) => {
          this._httpService.showHttpError(error);
          row.isDownloading = false;
          this._cdr.detectChanges();
        },
      });
  }

  private downloadAuditReport(row: TSignRequestTableRow): void {
    if (this.blockIfRequestOlderThanRetention(row)) return;
    // const appToken = this.resolveAppToken(row);
    // if (!appToken) {
    //   this._toastr.showError(Messages.MSG_ERROR_APP_TOKEN_NOT_FOUND);
    //   return;
    // }

    row.isDownloadingAudit = true;
    this._cdr.detectChanges();

    const apiUrl = this._httpService.createEndpoint(
      Constants.API_URI_SIGN_DOWNLOAD_AUDIT_REPORT
    );

    this._httpService
      .post(apiUrl, {
        // appToken,
        signRequestId: row.SignRequestId,
      })
      .subscribe({
        next: (response: unknown) => {
          if (
            !this.saveDownloadResponseAsFile(
              response,
              `Audit-Report-${row.RequestNo}.pdf`
            )
          ) {
            if (!this.showResponseFailRules(response)) {
              this._toastr.showError(Messages.MSG_ERROR_UNEXPECTED);
            }
          } else {
            this._toastr.showSuccess(Messages.MSG_SUCCESS_EXPORT_AUDIT_REPORT);
          }
          row.isDownloadingAudit = false;
          this._cdr.detectChanges();
        },
        error: (error) => {
          this._httpService.showHttpError(error);
          row.isDownloadingAudit = false;
          this._cdr.detectChanges();
        },
      });
  }

  private openRecallSignRequestDialog(row: TSignRequestTableRow): void {
    if (this.blockIfRequestOlderThanRetention(row)) return;
    const dialogRef = this._dialog.open(RecallSignRequestDialogComponent, {
      panelClass: "rounded-dialog",
      backdropClass: "blur-backdrop",
      width: "min(560px, 96vw)",
      autoFocus: "first-tabbable",
      data: { defaultRecalledBy: this.getRecalledByLabel() },
    });

    dialogRef.afterClosed().subscribe((result: IRecallSignRequestDialogResult | undefined) => {
      if (!result) return;

      // const appToken = this.resolveAppToken(row);
      // if (!appToken) {
      //   this._toastr.showError(Messages.MSG_ERROR_APP_TOKEN_NOT_FOUND);
      //   return;
      // }

      this.submitRecallSignRequest(row, result);
    });
  }

  private submitRecallSignRequest(
    row: TSignRequestTableRow,
    result: IRecallSignRequestDialogResult
  ): void {
    row.isRecalling = true;
    this._cdr.detectChanges();

    const apiUrl = this._httpService.createEndpoint(
      Constants.API_URI_RECALL_SIGN_REQUEST
    );

    this._httpService
      .post(apiUrl, {
        signRequestId: row.SignRequestId,
        // appToken,
        recallReason: result.recallReason,
        recalledBy: result.recalledBy,
      })
      .subscribe({
        next: () => {
          this._toastr.showSuccess(Messages.MSG_SUCCESS_RECALL_SIGN_REQUEST);
          row.isRecalling = false;
          this.getAllRequests();
        },
        error: (error) => {
          this._httpService.showHttpError(error);
          row.isRecalling = false;
          this._cdr.detectChanges();
        },
      });
  }

  private getRecalledByLabel(): string {
    const user = this._authService.getUserDetails();
    if (!user) return "";
    const name = [user.FirstName, user.LastName]
      .filter((part) => !!part?.trim())
      .join(" ")
      .trim();
    return name || user.Email || "";
  }

  private showResponseFailRules(response: unknown): boolean {
    const res = response as {
      IsSuccess?: boolean;
      FailRules?: Array<{ Msg?: string }>;
    };
    if (res?.IsSuccess !== false && !res?.FailRules?.length) {
      return false;
    }
    const rules = res.FailRules ?? [];
    let shown = false;
    rules.forEach((rule) => {
      const msg = rule?.Msg?.trim();
      if (msg) {
        this._toastr.showError(msg);
        shown = true;
      }
    });
    return shown;
  }

  private saveDownloadResponseAsFile(
    response: unknown,
    fallbackFileName: string
  ): boolean {
    const res = response as {
      IsSuccess?: boolean;
      FailRules?: Array<{ Msg?: string }>;
      Data?: Record<string, unknown>;
      data?: Record<string, unknown>;
    };

    if (res?.IsSuccess === false) {
      return false;
    }

    const data = res?.Data ?? res?.data;
    if (!data || typeof data !== "object") {
      return false;
    }

    const rawContent =
      data["DocContent"] ??
      data["docContent"] ??
      data["DocContentStr"] ??
      data["docContentStr"];

    if (typeof rawContent !== "string" || !rawContent.trim()) {
      return false;
    }

    const blob = this.base64ToPdfBlob(rawContent.trim());
    if (!blob) {
      return false;
    }

    const docName = data["DocName"] ?? data["docName"];
    const fileName =
      typeof docName === "string" && docName.trim()
        ? docName.trim()
        : fallbackFileName;

    FileSaver.saveAs(
      blob,
      fileName.toLowerCase().endsWith(".pdf") ? fileName : `${fileName}.pdf`
    );
    return true;
  }

  private base64ToPdfBlob(base64: string): Blob | null {
    try {
      const normalized = base64.includes(",") ? base64.split(",")[1]! : base64;
      const byteCharacters = atob(normalized);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      return new Blob([new Uint8Array(byteNumbers)], {
        type: "application/pdf",
      });
    } catch {
      return null;
    }
  }
}



